import React, { useEffect, useRef, useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useAltusSourceSettingsText,
  useAltusSourceSettingsViewModel,
} from "@onetasklist/shared/sources/altus";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface AltusSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const AltusSourceSettings: React.FC<AltusSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useAltusSourceSettingsText(t);
  const vm = useAltusSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [bucketQuery, setBucketQuery] = useState("");
  const [doneBucketQuery, setDoneBucketQuery] = useState("");
  const [defaultBucketQuery, setDefaultBucketQuery] = useState("");
  const onSearchProjectsRef = useRef(vm.onSearchProjects);

  useEffect(() => {
    onSearchProjectsRef.current = vm.onSearchProjects;
  }, [vm.onSearchProjects]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void onSearchProjectsRef.current(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  if (!vm.isVisible) {
    return null;
  }

  return (
    <>
      {!vm.isAuthorisedProfileReady && (
        <Text>{text.unauthorisedWarningText}</Text>
      )}

      {vm.isAuthorisedProfileReady && (
        <SettingsSectionCard
          sectionOffset={sectionOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.altus.sectionProjects",
          )}
        >
          {vm.environmentDisplayName && vm.environmentUrl && (
            <Field orientation="horizontal" label={text.environmentLabelText}>
              <a href={vm.environmentUrl} target="_blank" rel="noreferrer">
                {vm.environmentDisplayName}
              </a>
            </Field>
          )}

          <Field
            orientation="horizontal"
            label={text.projectFilterLabelText}
            hint={text.projectFilterHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedProjectIds}
              query={searchTerm}
              placeholder={text.projectSearchPlaceholderText}
              options={vm.projects.map((project) => ({
                id: project.sensei_projectid,
                label: project.sensei_name ?? project.sensei_projectid,
              }))}
              onQueryChange={setSearchTerm}
              onSelectionChange={vm.onSelectProjects}
              minQueryLength={2}
              typeToSearchText={text.projectFilterHintText}
              isLoading={!vm.projectsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.assignedToMeLabelText}
            hint={text.assignedToMeHintText}
          >
            <Checkbox checked disabled />
          </Field>
        </SettingsSectionCard>
      )}

      {vm.isAuthorisedProfileReady && vm.isBucketMode && (
        <SettingsSectionCard
          sectionOffset={sectionOffset + 1}
          title={t(
            "board.connections.catalogue.sourceSettings.altus.sectionBuckets",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.bucketsToLoadLabelText}
            hint={text.bucketsToLoadHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedBucketIds}
              query={bucketQuery}
              placeholder={text.bucketsToLoadPlaceholderText}
              options={vm.buckets.map((bucket) => ({
                id: bucket.sensei_bucketid,
                label: bucket.sensei_name ?? bucket.sensei_bucketid,
              }))}
              onQueryChange={setBucketQuery}
              onSelectionChange={vm.onSelectBuckets}
              minQueryLength={0}
              typeToSearchText={text.bucketsToLoadHintText}
              isLoading={!vm.bucketsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.doneBucketLabelText}
            hint={text.doneBucketHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedDoneBucketId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDoneBucketId
              }
              selectedLabel={
                vm.buckets.find(
                  (bucket) =>
                    bucket.sensei_bucketid === vm.selectedDoneBucketId,
                )?.sensei_name
              }
              query={doneBucketQuery}
              placeholder={text.doneBucketPlaceholderText}
              options={vm.buckets.map((bucket) => ({
                id: bucket.sensei_bucketid,
                label: bucket.sensei_name ?? bucket.sensei_bucketid,
              }))}
              onQueryChange={setDoneBucketQuery}
              onSelect={vm.onSelectDoneBucket}
              clearSelectionText={text.doneBucketPlaceholderText}
              isLoading={!vm.bucketsLoaded}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.defaultBucketLabelText}
            hint={text.defaultBucketHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedDefaultBucketId === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDefaultBucketId
              }
              selectedLabel={
                vm.buckets.find(
                  (bucket) =>
                    bucket.sensei_bucketid === vm.selectedDefaultBucketId,
                )?.sensei_name
              }
              query={defaultBucketQuery}
              placeholder={text.defaultBucketPlaceholderText}
              options={vm.buckets.map((bucket) => ({
                id: bucket.sensei_bucketid,
                label: bucket.sensei_name ?? bucket.sensei_bucketid,
              }))}
              onQueryChange={setDefaultBucketQuery}
              onSelect={vm.onSelectDefaultBucket}
              clearSelectionText={text.defaultBucketPlaceholderText}
              isLoading={!vm.bucketsLoaded}
            />
          </Field>
        </SettingsSectionCard>
      )}

      {vm.isAuthorisedProfileReady && vm.isSearchingProjects && (
        <Text>{text.searchingProjectsText}</Text>
      )}
      {vm.isAuthorisedProfileReady && vm.isBucketMode && !vm.bucketsLoaded && (
        <Text>{text.loadingBucketsText}</Text>
      )}
      {vm.isAuthorisedProfileReady && vm.hasError && (
        <Text>{vm.errorMessage ?? text.loadErrorText}</Text>
      )}
    </>
  );
};
